import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getCabVehicle, submitCabEnquiry } from '../../api/cabsPublic';
import CabsBreadcrumbs from './CabsBreadcrumbs';

const CabsVehicleEnquiry = () => {
  const { vehicleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    customerName: '',
    phone: '',
    email: '',
    budget: '',
    notes: '',
    consent: false,
  });

  const searchParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const tripTypeParam = searchParams.get('tripType');
  const tripType = tripTypeParam === 'roundtrip' ? 'ROUNDTRIP' : 'ONEWAY';
  const pickupLocationId = searchParams.get('pickupLocationId') || '';
  const dropLocationId = searchParams.get('dropLocationId') || '';
  const pickupDate = searchParams.get('pickupDate') || '';
  const pickupTime = searchParams.get('pickupTime') || '';
  const returnDate = searchParams.get('returnDate') || '';
  const passengers = searchParams.get('passengers') || 1;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getCabVehicle(vehicleId);
        if (!cancelled) setVehicle(res.data || res || null);
      } catch {
        if (!cancelled) setVehicle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await submitCabEnquiry({
        vehicleId,
        tripType,
        pickupLocationId,
        dropLocationId,
        pickupDate,
        pickupTime,
        returnDate: tripType === 'ROUNDTRIP' ? returnDate : undefined,
        passengers: Number(passengers || 1),
        customerName: form.customerName,
        phone: form.phone,
        email: form.email || undefined,
        budget: form.budget ? Number(form.budget) : undefined,
        notes: form.notes || undefined,
        consent: form.consent,
        sourcePage: 'vehicle_detail',
      });
      const leadId = res?.data?.id || res?.id || '';
      const params = new URLSearchParams(location.search || '');
      if (leadId) params.set('leadId', leadId);
      params.set('type', 'cab');
      navigate(`/cabs/enquiry/success?${params.toString()}`);
    } catch (err) {
      setError(err.message || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading enquiry...
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Vehicle not found.
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <CabsBreadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Cabs', to: '/cabs' },
          { label: 'Listing', to: '/cabs/list' },
          { label: vehicle.name, to: `/cabs/${vehicleId}` },
          { label: 'Enquiry' },
        ]}
      />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Cab enquiry</h1>
            <p className="text-sm text-slate-600">Share trip details for {vehicle.name}.</p>
          </div>
          <Link
            to={`/cabs/${vehicleId}${location.search}`}
            className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600"
          >
            Back to details
          </Link>
        </div>

        {error && <div className="mt-4 rounded-xl bg-rose-50 p-3 text-xs text-rose-700">{error}</div>}

        <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4 text-xs text-slate-600">
          <p className="font-semibold text-slate-900">Trip summary</p>
          <p className="mt-2">{tripType === 'ROUNDTRIP' ? 'Round trip' : 'One way'}</p>
          <p>
            Pickup: {pickupDate || 'Not set'} {pickupTime || ''}
          </p>
          {tripType === 'ROUNDTRIP' && <p>Return: {returnDate || 'Not set'}</p>}
          <p>Passengers: {passengers}</p>
        </div>

        <form className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Name *</label>
            <input
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.customerName}
              onChange={(e) => handleChange('customerName', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Phone *</label>
            <input
              type="tel"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              inputMode="tel"
              pattern="[0-9+\-\s()]{7,20}"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email *</label>
            <input
              type="email"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Budget</label>
            <input
              type="number"
              min="0"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.budget}
              onChange={(e) => handleChange('budget', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Notes</label>
            <textarea
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              rows="4"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>
          <div className="md:col-span-2 flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => handleChange('consent', e.target.checked)}
              required
            />
            <span>I agree to be contacted about this enquiry.</span>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white"
            >
              {submitting ? 'Submitting...' : 'Submit enquiry'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default CabsVehicleEnquiry;
